/* Nuxt auth doesn't seem to have a way to do a PKCE grant flow
 * without replacing the login session, so we do the flow by hand here.
 * We use this for enrolling and disabling 2FA. We don't want to request
 * the necessary scopes during login, we only want to request them when
 * the user needs them to enable or disable 2FA. We also don't want to
 * store the 2FA access token long term.
 *
 * Some of this code is copied from here: https://github.com/aaronpk/pkce-vanilla-js/blob/master/index.html
 * Epic hacks are used just below to include the license text and trick the bundler into not stripping it out.
 */

const license = `
MIT License

Copyright (c) 2019 Aaron Parecki

Permission is hereby granted, free of charge, to any person obtaining a copy
of this software and associated documentation files (the "Software"), to deal
in the Software without restriction, including without limitation the rights
to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
copies of the Software, and to permit persons to whom the Software is
furnished to do so, subject to the following conditions:

The above copyright notice and this permission notice shall be included in all
copies or substantial portions of the Software.

THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN THE
SOFTWARE.`.slice(-1);

void (() => license)();

import { Context } from '@nuxt/types/app';

const localStorageKeys = {
  state: 'pkce_state',
  verifier: 'pkce_code_verifier',
  destinationPath: 'pkce_redirect_destination_path',
  retrievedAccessToken: 'pkce_access_token',
};

function generateRandomString() {
  const array = new Uint32Array(28);
  window.crypto.getRandomValues(array);
  return Array.from(array, dec => ('0' + dec.toString(16)).substr(-2)).join('');
}

function sha256(plain: string) {
  const encoder = new TextEncoder();
  const data = encoder.encode(plain);
  return window.crypto.subtle.digest('SHA-256', data);
}

// Base64-urlencodes the input string
function base64urlencode(buf: ArrayBuffer) {
  // Convert the ArrayBuffer to string using Uint8 array to conver to what btoa accepts.
  // btoa accepts chars only within ascii 0-255 and base64 encodes them.
  // Then convert the base64 encoded to base64url encoded
  //   (replace + with -, replace / with _, trim trailing =)
  return btoa(String.fromCharCode(...new Uint8Array(buf)))
    .replace(/\+/g, '-')
    .replace(/\//g, '_')
    .replace(/=+$/, '');
}

async function pkceChallengeFromVerifier(v: string) {
  const hashed = await sha256(v);
  return base64urlencode(hashed);
}

export async function pkceCallbackMiddleware(context: Context) {
  const searchParams = new URLSearchParams(window.location.search.substring(1));
  const code = searchParams.get('code');
  const state = searchParams.get('state');
  const domain = process.env.auth0Domain;
  const audience = `https://${process.env.auth0MfaDomain}/mfa/`;
  const clientId = process.env.auth0ClientId ?? '';
  const verifier = localStorage.getItem(localStorageKeys.verifier) ?? '';

  if (!code || !state) {
    return context.redirect('/');
  }

  if (state !== localStorage.getItem(localStorageKeys.state)) {
    return context.redirect('/');
  }

  const response = await fetch(`https://${domain}/oauth/token`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      code,
      client_id: clientId,
      redirect_uri: `${window.location.origin}/pkce-callback`,
      response_type: 'code',
      audience,
      grant_type: 'authorization_code',
      code_verifier: verifier,
    }),
  });

  if (!response.ok) {
    return context.redirect('/');
  }

  const responseBody = await response.json();
  localStorage.setItem(
    localStorageKeys.retrievedAccessToken,
    responseBody.access_token,
  );

  return context.redirect(
    localStorage.getItem(localStorageKeys.destinationPath) ?? '/',
  );
}

export async function requestAccessToken(
  redirectPath: string,
  scopes: string[],
  additionalParams?: Record<string, string>,
) {
  const redirectUrl = `${window.location.origin}/pkce-callback`;
  const clientId = process.env.auth0ClientId;
  const audience = `https://${process.env.auth0MfaDomain}/mfa/`;
  const domain = process.env.auth0Domain;

  const state = generateRandomString();
  localStorage.setItem(localStorageKeys.state, state);

  const verifier = generateRandomString();
  localStorage.setItem(localStorageKeys.verifier, verifier);

  localStorage.setItem(localStorageKeys.destinationPath, redirectPath);

  const codeChallenge = await pkceChallengeFromVerifier(verifier);

  const params = new Map();
  params.set('protocol', 'oauth2');
  params.set('scope', scopes.join(' '));
  params.set('access_type', '');
  params.set('response_type', 'code');
  params.set('state', state);
  params.set('code_challenge_method', 'S256');
  params.set('audience', audience);
  params.set('code_challenge', codeChallenge);
  params.set('client_id', clientId);
  params.set('redirect_uri', redirectUrl);
  params.set('prompt', 'login');

  if (additionalParams) {
    Object.keys(additionalParams).forEach(key => {
      params.set(key, additionalParams[key]);
    });
  }

  const urlParams = new URLSearchParams(Object.fromEntries(params)).toString();

  const path = `https://${domain}/authorize?${urlParams}`;
  window.location.href = path;
}

export function consumeAccessToken() {
  const token = localStorage.getItem('pkce_access_token');
  localStorage.removeItem('pkce_access_token');
  return token;
}
